<template>
  <VacancyContainer
    :id="vacancyId"
    v-slot="{ company, vacancy }"
    @ready="handleVacancyContainerReady"
  >
    <div v-if="!applySuccess">
      <VRow class="justify-center font-weight-medium">
        <VCol
          cols="12"
          lg="10"
          :class="{'pt-0': $route.meta.iframe}"
        >
          <h1 class="candidate-profile-title text-center pt-3 pt-md-5 pt-lg-8 mb-4 px-4 px-md-8 px-lg-0">
            {{ $t('fillForm') }}
          </h1>
          <p v-if="!$route.meta.iframe" class="body-2 mb-6 px-4 px-md-8 px-lg-0">
            {{ $t('recommendation') }}
          </p>
          <VForm v-model="isValid" v-scroll-to-first-error class=" px-4 px-md-8 px-lg-0">
            <VRow>
              <VCol cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <VTextField
                  id="lastName"
                  v-model="inputs.lastName"
                  :error-messages="profileError.errors.lastName"
                  outlined
                  dense
                  autocomplete="family-name"
                  @input="profileError.errors.lastName = null"
                >
                  <template v-slot:label>
                    {{ $t('lastName') }} <span class="red--text">*</span>
                  </template>
                </VTextField>
              </VCol>
              <VCol cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <VTextField
                  id="firstName"
                  v-model="inputs.firstName"
                  :error-messages="profileError.errors.firstName"
                  outlined
                  dense
                  autocomplete="given-name"
                  @input="profileError.errors.firstName = null"
                >
                  <template v-slot:label>
                    {{ $t('firstName') }} <span class="red--text">*</span>
                  </template>
                </VTextField>
              </VCol>
              <VCol cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <VTextField
                  id="email"
                  v-model="inputs.email"
                  :disabled="!isEmailInputEnabled"
                  :error-messages="profileError.errors.email"
                  outlined
                  dense
                  type="email"
                  autocomplete="email"
                  @input="profileError.errors.email = null"
                >
                  <template v-slot:label>
                    {{ $t('email') }} <span class="red--text">*</span>
                  </template>
                </VTextField>
              </VCol>
              <VCol v-if="!isEmailInputEnabled && !$route.meta.iframe" cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <div class="d-block text-right mt-n6 pb-6">
                  <span
                    class="pseudo-link blue--text font-weight-light body-2"
                    @click="handleWrongEmailClick"
                  >
                    {{ $t('notMine') }}
                  </span>
                </div>
              </VCol>
              <VCol cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <PhoneNumberInput
                  v-model="inputs.phone"
                  required
                  :error-messages="profileError.errors.phone"
                  @input="profileError.errors.phone = null"
                />
              </VCol>
              <VCol cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <VTextField
                  id="location"
                  v-model="inputs.location"
                  :label="$t('city')"
                  :error-messages="profileError.errors.location"
                  :hint="inputLocationHint"
                  :counter="24"
                  :maxlength="24"
                  outlined
                  dense
                  autocomplete="address-level2"
                  @input="profileError.errors.location = null"
                />
              </VCol>
              <VCol cols="12" :sm="$route.meta.iframe ? 6 : 12" class="py-0">
                <div>
                  <VMenu
                    v-model="birthDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on: { click } }">
                      <VTextField
                        v-mask="birthDateMask"
                        :value="birthDate"
                        :label="$t('birthDate')"
                        :error-messages="profileError.errors.birthDate"
                        :rules="[validateBirthDate]"
                        outlined
                        dense
                        single-line
                        clearable
                        :prepend-inner-icon="calendarIcon"
                        :validate-on-blur="true"
                        autocomplete="off"
                        @click:prepend-inner="click"
                        @change="birthDate = $event"
                      />
                    </template>
                    <VDatePicker
                      ref="birthDatePicker"
                      v-model="inputs.birthDate"
                      :max="new Date().toISOString().substr(0, 10)"
                      min="1920-01-01"
                      first-day-of-week="1"
                      :locale="$i18n.locale"
                      @input="birthDateMenu = false"
                    />
                  </VMenu>
                </div>
              </VCol>
              <VCol
                v-for="(addField, index) in vacancyCandidateAdditionalFields"
                :key="index"
                cols="12"
                :sm="$route.meta.iframe ? 6 : 12"
                class="py-0"
              >
                <CandidateProfileFormAddInput
                  :add-field="addField"
                  :profile-error="profileError"
                  @setAdditionalValue="setAdditionalValue"
                />
              </VCol>
            </VRow>
            <div :class="$route.meta.iframe ? 'pb-0' : 'pb-2'">
              <div class="body-2 pb-2 d-none d-md-block">
                {{ $t('resumeTip') }}
              </div>
              <VFileInput
                v-if="!candidateResume"
                v-model="resume"
                :label="$t('resume')"
                outlined
                dense
                prepend-icon=""
                :prepend-inner-icon="mdiFileOutline"
                show-size
                persistent-hint
                :accept="resumeAvailableTypes"
                :rules="resumeRules"
                :error-messages="resumeError.errors.resume || resumeError.errorMessage"
                @change="resetError('resumeError')"
              />
              <div v-else>
                <span
                  class="pseudo-link blue--text font-weight-light mr-2"
                  @click="downloadResume"
                >
                  {{ $t('viewResume') }}
                </span>
                <VBtn
                  text
                  small
                  icon
                  color="red"
                  :loading="resumeDestroyLoading"
                  :disabled="isLoading"
                  @click="destroyResume"
                >
                  <VIcon>{{ destroyIcon }}</VIcon>
                </VBtn>
              </div>
              <div class="body-2 pt-2 d-md-none">
                {{ $t('resumeTip') }}
              </div>
            </div>
            <VTextarea
              v-if="isCoverLetterInputVisible"
              v-model="coverLetter"
              :label="$t('coverLetter')"
              rows="2"
              outlined
              :counter="10000"
              :error-messages="coverLetterError.errors.comment || coverLetterError.errorMessage"
              @input="handleCoverLetterInput"
            />
            <div class="font-weight-regular">
              <template v-if="!withoutInvitation">
                <VCheckbox
                  v-model="freeTimeCheckbox"
                  hide-details
                  class="checkbox1"
                  :ripple="false"
                >
                  <span slot="label">
                    {{ $t('haveTime') }}
                  </span>
                </VCheckbox>
              </template>
              <template v-if="!withoutInvitation">
                <VCheckbox
                  v-model="companyInfoCheckbox"
                  hide-details
                  :ripple="false"
                  class="mt-2 checkbox2"
                >
                  <div slot="label">
                    {{ $t('read') }}
                    <RouterLink
                      v-slot="{ href }"
                      :to="{
                        name: 'vacancy',
                        params: { companyId: vacancy.companyId, vacancyId: vacancy.id }
                      }"
                      target="_blank"
                    >
                      <a :href="href" target="_blank" @click.stop>{{ $t('vacancyText') }}</a>
                    </RouterLink>
                    {{ $t('and') }}
                    {{ $t('companyInfo') }}
                  </div>
                </VCheckbox>
              </template>
              <VCheckbox
                v-model="personalDataCheckbox"
                hide-details
                :ripple="false"
                class="mt-2 checkbox3"
              >
                <span slot="label">
                  {{ $t('permission') }}
                  <RouterLink
                    v-slot="{ href }"
                    :to="{ name: 'confidential' }"
                    target="_blank"
                  >
                    <a :href="href" target="_blank" @click.stop>{{ $t('personalData') }}</a>
                  </RouterLink>
                </span>
              </VCheckbox>
            </div>
            <VAlert
              v-if="hasErrorMessageOnly && firstErrorMessage"
              type="alert"
              :class="$route.meta.iframe ? 'my-4' : 'my-8'"
            >
              {{ firstErrorMessage }}
            </VAlert>
            <div class="d-flex">
              <VBtn
                color="success"
                :class="$route.meta.iframe ? 'mt-5 mx-auto' : 'my-8'"
                :disabled="!isSubmitButtonEnabled"
                :loading="isLoading"
                @click="submit"
              >
                {{ withoutInvitation ? $t('apply') : $t('continue') }}
              </VBtn>
            </div>
          </VForm>
        </VCol>
      </VRow>
    </div>
    <div v-else>
      <IframeApplySuccess :vacancy-id="vacancy.id" />
    </div>
  </VacancyContainer>
</template>

<script>
import { mdiClose, mdiCalendar, mdiFileOutline } from '@mdi/js';
import { mask } from 'vue-the-mask';
import { parse, format } from 'date-fns';
import IframeApplySuccess from '@/views/IframeApplySuccess';
import VacancyContainer from '@/components/Vacancy/VacancyContainer';
import ApiMixin from '../mixins/ApiMixin';
import RecaptchaMixin from '../mixins/RecaptchaMixin';
import ScrollToFirstError from '../directives/ScrollToFirstError';
import { candidateInvitationType } from '../enums';
import { DATE_FORMATS, DATE_MASKS } from '../date-utils';
import { safeGet, normalizePhoneNumber } from '../functions';
import useLocale from '@/compositions/useLocale';
import PhoneNumberInput from '../inputs/PhoneNumberInput.vue';
import CandidateProfileFormAddInput from '../inputs/CandidateProfileFormAddInput.vue';

export default {
  name: 'IframeCandidateProfileForm',
  components: {
    VacancyContainer, IframeApplySuccess, PhoneNumberInput, CandidateProfileFormAddInput,
  },
  directives: { mask, ScrollToFirstError },
  mixins: [ApiMixin, RecaptchaMixin],
  setup() {
    const { setLocaleFromQuery } = useLocale();
    return {
      setLocaleFromQuery,
    };
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      inputs: this.getInitialInputs(),
      coverLetter: '',
      forceCoverLetter: false,
      resume: null,
      freeTimeCheckbox: false,
      noUniversityCheckbox: false,
      companyInfoCheckbox: false,
      personalDataCheckbox: false,
      birthDateMenu: false,
      profileError: this.getInitialErrorData(),
      resumeError: this.getInitialErrorData(),
      coverLetterError: this.getInitialErrorData(),
      isLoading: false,
      resumeDestroyLoading: false,
      resumeAvailableTypes: '.pdf,.doc,.docx,.odt,.rtf',
      resumeRules: [
        value => !value || value.size < 5000000 || `${this.$t('sizeWarning')} 5 MB.`,
      ],
      isValid: false,
      destroyIcon: mdiClose,
      calendarIcon: mdiCalendar,
      mdiFileOutline,
      applySuccess: false,
      candidate: null,
      withoutInvitation: true,
    };
  },
  computed: {
    vacancy() {
      return safeGet(this.$store.state.vacancy, ['vacancy']);
    },
    isEmailInputEnabled() {
      return !this.candidate;
    },
    isCoverLetterInputVisible() {
      return !this.candidate || this.coverLetterError.errorMessage || this.forceCoverLetter;
    },
    birthDate: {
      get() {
        if (!this.inputs.birthDate) {
          return null;
        }

        return format(
          parse(this.inputs.birthDate, DATE_FORMATS.server.short, new Date()),
          DATE_FORMATS[this.$i18n.locale].short,
        );
      },
      set(value) {
        if (this.validateBirthDate(value) !== true) {
          return;
        }

        if (value === null) {
          this.inputs.birthDate = null;
          return;
        }

        this.inputs.birthDate = format(
          parse(value, DATE_FORMATS[this.$i18n.locale].short, new Date()),
          DATE_FORMATS.server.short,
        );
      },
    },
    birthDateMask() {
      return DATE_MASKS[this.$i18n.locale].short;
    },
    isSubmitButtonEnabled() {
      return ((this.freeTimeCheckbox && this.companyInfoCheckbox) || this.withoutInvitation)
        && this.personalDataCheckbox
        && this.isValid;
    },
    candidateResume() {
      return safeGet(this.candidate, ['resume']);
    },
    hasErrorMessageOnly() {
      return !Object.keys(this.profileError.errors).length
        && !Object.keys(this.resumeError.errors).length
        && !Object.keys(this.coverLetterError.errors).length
        && (
          this.profileError.errorMessage
          || this.resumeError.errorMessage
          || this.coverLetterError.errorMessage
        );
    },
    firstErrorMessage() {
      return this.profileError.errorMessage
        || this.resumeError.errorMessage
        || this.coverLetterError.errorMessage;
    },
    vacancyCandidateAdditionalFields() {
      return this.vacancy?.data?.candidateAdditionalFields;
    },
    inputLocationHint() {
      return this.inputs?.location?.length >= 24 ? `${this.$t('charactersLimit')} - 24` : '';
    },
  },
  watch: {
    birthDateMenu(val) {
      if (val) {
        setTimeout(() => { this.$refs.birthDatePicker.activePicker = 'YEAR'; });
      }
    },
  },
  async mounted() {
    await this.fetchVacancy();
    if (localStorage.getItem(`vacancy_${this.vacancyId}_current_candidate`)) {
      await this.$store.dispatch('candidate/fetchData', {
        vacancyId: this.vacancyId,
      });
      return this.$router.push({
        name: 'interviewIframe',
        params: {
          vacancyId: this.vacancyId,
        },
      });
    }
    this.setAdditionalInputs();
    if (this.candidate) {
      this.inputs = {
        firstName: this.candidate.firstName,
        lastName: this.candidate.lastName,
        location: this.candidate.location,
        email: this.candidate.email,
        phone: this.candidate.phone,
        birthDate: this.candidate.birthDate,
      };
    }
  },
  created() {
    this.calculateHeight();
  },
  updated() {
    this.changeHeight();
  },
  methods: {
    async fetchVacancy() {
      await this.$store.dispatch('vacancy/fetchVacancy', this.vacancyId);
    },
    handleWrongEmailClick() {
      this.$store.commit('candidate/logout');
      this.reset();
    },
    handleCoverLetterInput() {
      this.forceCoverLetter = true;
      this.coverLetterError = this.getInitialErrorData();
    },
    reset() {
      this.inputs = this.getInitialInputs();
      this.coverLetter = '';
    },
    setAdditionalInputs() {
      if (!this.vacancyCandidateAdditionalFields) {
        return;
      }
      const addFields = {};
      this.vacancyCandidateAdditionalFields.forEach((field) => {
        addFields[field.value] = null;
      });
      this.inputs.additionalFields = {
        ...addFields,
      };
    },
    getInitialInputs() {
      return process.env.NODE_ENV === 'development' ? {
        invitationType: candidateInvitationType.ONLINE_INTERVIEW,
        firstName: 'Иван',
        lastName: 'В валенках',
        location: 'Сургут',
        email: 'la@huntica.works',
        phone: '+79258301226',
        birthDate: null,
      } : {
        invitationType: candidateInvitationType.ONLINE_INTERVIEW,
        firstName: null,
        lastName: null,
        location: null,
        email: null,
        phone: null,
        birthDate: null,
      };
    },
    validateBirthDate(value) {
      if (value === null) {
        return true;
      }

      if (value.length !== DATE_FORMATS[this.$i18n.locale].short.length) {
        return this.$t('wrongDate');
      }

      try {
        format(
          parse(value, DATE_FORMATS[this.$i18n.locale].short, new Date()),
          DATE_FORMATS.server.short,
        );
        return true;
      } catch (e) {
        return this.$t('wrongDate');
      }
    },
    async submit() {
      await this.create();
      this.$nextTick(() => {
        setTimeout(() => {
          // eslint-disable-next-line no-restricted-globals
          parent.postMessage('huntica-interview-form', '*');
        }, 1000);
      });
    },
    async create() {
      this.isLoading = true;
      try {
        const recaptchaToken = await this.getRecaptchaToken('candidateCreate');
        const candidate = await this.createCandidate(recaptchaToken);
        await this.$store.dispatch('candidate/loginByHashId', {
          hashId: candidate.hashId,
          vacancyId: this.vacancyId,
        });
        await this.$store.dispatch('candidate/fetchData', { vacancyId: this.vacancyId });
        await this.postActions(candidate);
        this.applySuccess = true;
      } catch (e) {
        if (this.hasErrorMessageOnly) {
          this.$toast.error(e.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async createCandidate(recaptchaToken) {
      return !this.withoutInvitation
        ? this.withApiErrorHandling(async () => this.$store.dispatch('candidate/create', {
          vacancyId: this.vacancyId,
          params: { ...this.getSubmitParams(), recaptchaToken },
        }), 'profileError')
        : this.withApiErrorHandling(async () => this.$store.dispatch('candidate/createWithoutInvitation', {
          vacancyId: this.vacancyId,
          params: { ...this.getSubmitParams(), recaptchaToken, sendEmail: true },
        }), 'profileError');
    },
    getSubmitParams() {
      return { ...this.inputs, phone: normalizePhoneNumber(this.inputs.phone) };
    },
    async postActions(candidate) {
      if (this.resume) {
        await this.withApiErrorHandling(async () => this.$store.dispatch('candidate/storeResume', {
          candidateId: candidate.id,
          resume: this.resume,
        }), 'resumeError');
      }
      if (this.coverLetter !== '') {
        await this.withApiErrorHandling(async () => this.$store.dispatch('candidate/sendCoverLetter', {
          candidateId: candidate.id,
          coverLetter: this.coverLetter,
        }), 'coverLetterError');
      }
    },
    downloadResume() {
      window.open(this.candidate.resume.file.url);
    },
    async destroyResume() {
      this.resume = null;
      this.resumeDestroyLoading = true;
      try {
        await this.$store.dispatch('candidate/destroyResume', this.candidate.resume.id);
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.resumeDestroyLoading = false;
      }
    },
    handleVacancyContainerReady({ vacancy }) {
      this.pageTitle = `${this.$t('pageTitle')} ${vacancy.title}`;
      this.setLocaleFromQuery(this.$route);
    },
    calculateHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          let height = document.documentElement.scrollHeight;
          if (document.documentElement.offsetHeight > height) {
            height = document.documentElement.offsetHeight;
          }
          // eslint-disable-next-line no-restricted-globals
          parent.postMessage({ height }, '*');
        }, 1000);
      });
    },
    changeHeight() {
      if (this.$route.meta.iframe) {
        this.calculateHeight();
      }
    },
    setAdditionalValue(value, inputName) {
      if (this.inputs.additionalFields.hasOwnProperty(inputName)) {
        this.inputs.additionalFields[inputName] = value;
      }
      if (this.profileError.errors.hasOwnProperty(`additionalFields.${inputName}`)) {
        this.profileError.errors[`additionalFields.${inputName}`] = null;
      }
    },
    setCandidateFields() {
      if (this.candidate) {
        this.inputs = {
          firstName: this.candidate.firstName,
          lastName: this.candidate.lastName,
          location: this.candidate.location,
          email: this.candidate.email,
          phone: this.candidate.phone,
          birthDate: this.candidate.birthDate,
        };
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.candidate-profile-title {
  font-size: 1.5rem;
  font-weight: 400;
}
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .candidate-profile-title {
    font-size: 1.25rem;
  }
}
::v-deep {
  .v-text-field--outlined fieldset {
    border-color: #C2C2C2;
    border-radius: 4px;
  }

  .v-input--checkbox {
    .v-input__slot {
      align-items: flex-start;

      .v-label {
        top: 3px;
      }
    }
  }

  .v-text-field.v-input--dense .v-input__prepend-inner .v-input__icon > .v-icon,
  .v-text-field.v-input--dense .v-input__append-inner .v-input__icon > .v-icon {
    margin-top: 2px;
  }
}
</style>

<!-- eslint-disable max-len -->
<i18n locale="en" lang="yaml">
fillForm: Fill in the form
recommendation: We recommend that you fill in all form fields. The more data you provide to the employer, the better your chances are for making it to the next stage.
lastName: Last name
firstName: First name
city: City
email: E-mail
notMine: This is not my e-mail
birthDate: Birth date
resumeTip: Not submitting a resume significantly lowers your chances of being hired
resume: Resume
viewResume: View resume
coverLetter: Cover letter
haveTime: I have 20 minutes of free time
read: I have read
vacancyText: job text
and: and
companyInfo: information about the company
permission: I consent to the storage and processing of my
personalData: personal data
continue: Continue
sizeWarning: The resume should not be larger than
wrongDate: Wrong date format
apply: Apply
charactersLimit: The maximum number of characters is
</i18n>

<i18n locale="ru" lang="yaml">
fillForm: Заполните анкету
recommendation: Рекомендуем заполнить все поля формы. Чем больше данных вы предоставите работодателю для принятия решения, тем выше ваши шансы пройти на следующий этап.
lastName: Фамилия
firstName: Имя
city: Город
email: E-mail
notMine: Это не моя почта
birthDate: Дата рождения
resumeTip: Наличие резюме в отклике существенно повышает шансы на трудоустройство.
resume: Резюме
viewResume: Посмотреть резюме
coverLetter: Сопроводительное письмо
haveTime: У меня есть 30 минут свободного времени
read: Я прочитал
vacancyText: текст вакансии
and: и ознакомился с
companyInfo: информацией о компании
permission: Я даю согласие на хранение и обработку моих
personalData: персональных данных
continue: Далее
sizeWarning: Размер резюме не должне превышать
wrongDate: Неверная дата
apply: Откликнуться на вакансию
charactersLimit: Максимальное количество символов
</i18n>
<!-- eslint-enable max-len -->
